// @ts-check
// Taylor's Boats — YouTube view tracking.
//
// Listens for `onStateChange === PLAYING` on every embedded YouTube iframe
// and pushes a `youtube_view` event to `window.dataLayer` (so GTM forwards
// to GA4) plus `window._learnq` (Klaviyo Onsite, if loaded by GTM).
//
// Section/template Liquid is responsible for two things:
//   1. Building embed URLs with `enablejsapi=1` (YT API requirement).
//   2. On boat PDPs, setting `window.taylorsBoats.boat = { product_id,
//      stock_number, year, make, model }` so the event payload carries
//      boat metadata. Other surfaces fire with empty boat fields.
//
// Debounce: same video_id within the same page-view fires once. Navigating
// to a new page resets the de-dupe set.

(function () {
  if (window.tbYtTrackingLoaded) return;
  window.tbYtTrackingLoaded = true;

  var PLAYING = 1;
  var seen = new Set();
  var pending = [];
  var apiReady = false;
  var apiLoading = false;

  // Consent gates. When the consent bridge (`window.tbConsent`, set by
  // snippets/tb-consent.liquid) is absent — e.g. GTM/banner not yet wired —
  // default to allowed so behavior is unchanged. When present, honor it:
  // the iframe API + `youtube_view` are analytics; the Klaviyo Onsite mirror
  // is marketing.
  function analyticsAllowed() {
    var tb = window.tbConsent;
    return tb && tb.analyticsAllowed ? tb.analyticsAllowed() : true;
  }
  function marketingAllowed() {
    var tb = window.tbConsent;
    return tb && tb.marketingAllowed ? tb.marketingAllowed() : true;
  }
  function onConsentChange(fn) {
    var tb = window.tbConsent;
    if (tb && tb.onChange) tb.onChange(fn);
  }

  function isYt(el) {
    return (
      el instanceof HTMLIFrameElement &&
      // Match the host anywhere — embeds use `www.youtube.com` / `www.youtube-nocookie.com`,
      // so a leading-slash anchor (`/youtube.com`) would never match the subdomain form.
      /(?:youtube|youtube-nocookie)\.com\/embed\//.test(el.src || '')
    );
  }

  function getVideoId(iframe) {
    var m = (iframe.src || '').match(/\/embed\/([^?&/]+)/);
    return m ? m[1] : '';
  }

  function loadApi() {
    if (apiLoading || apiReady) return;
    // Don't pull in YouTube's iframe API (or its cookies) until analytics
    // consent is in place. If it isn't yet, retry when the visitor decides.
    if (!analyticsAllowed()) {
      onConsentChange(loadApi);
      return;
    }
    apiLoading = true;
    var s = document.createElement('script');
    s.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(s);
  }

  function emit(videoId, videoTitle) {
    if (!videoId || seen.has(videoId)) return;
    seen.add(videoId);

    var tb = window.taylorsBoats || {};
    var boat = tb.boat || {};

    var payload = {
      event: 'youtube_view',
      video_id: videoId,
      video_title: videoTitle || '',
      boat_product_id: boat.product_id || '',
      boat_stock_number: boat.stock_number || '',
      boat_year: boat.year || '',
      boat_make: boat.make || '',
      boat_model: boat.model || '',
    };

    // `youtube_view` → GA4 is analytics; the Klaviyo Onsite mirror is
    // marketing. Gate each on its own consent category.
    if (analyticsAllowed()) {
      (window.dataLayer = window.dataLayer || []).push(payload);
    }

    if (
      marketingAllowed() &&
      window._learnq &&
      typeof window._learnq.push === 'function'
    ) {
      try {
        window._learnq.push(['track', 'YouTube View', payload]);
      } catch (e) {}
    }
  }

  function attach(iframe) {
    if (iframe.__tbYt) return;
    iframe.__tbYt = true;
    if (!iframe.id) {
      iframe.id = 'tb-yt-' + Math.random().toString(36).slice(2, 8);
    }

    var fire = function () {
      emit(getVideoId(iframe), iframe.title || iframe.getAttribute('aria-label'));
    };

    var init = function () {
      if (!window.YT || !window.YT.Player) return;
      try {
        new window.YT.Player(iframe, {
          events: {
            // Autoplay embeds (the video modal sets `src` with `autoplay=1`)
            // are frequently already playing by the time the API binds —
            // especially the first video, when the IFrame API cold-loads —
            // so there's no transition into PLAYING for onStateChange to see.
            // Catch that case by checking the state once the player is ready.
            onReady: function (e) {
              try {
                if (e.target.getPlayerState() === PLAYING) fire();
              } catch (err) {}
            },
            onStateChange: function (e) {
              if (e.data === PLAYING) fire();
            },
          },
        });
      } catch (err) {}
    };

    if (apiReady) {
      init();
    } else {
      pending.push(init);
      loadApi();
    }
  }

  window.onYouTubeIframeAPIReady = function () {
    apiReady = true;
    pending.forEach(function (fn) {
      fn();
    });
    pending.length = 0;
  };

  function scan(root) {
    root.querySelectorAll('iframe').forEach(function (iframe) {
      if (isYt(iframe)) attach(iframe);
    });
  }

  var obs = new MutationObserver(function (records) {
    records.forEach(function (r) {
      r.addedNodes.forEach(function (node) {
        if (node instanceof HTMLIFrameElement) {
          if (isYt(node)) attach(node);
        } else if (node instanceof Element) {
          scan(node);
        }
      });
      if (r.type === 'attributes' && r.target instanceof HTMLIFrameElement) {
        if (isYt(r.target)) attach(r.target);
      }
    });
  });

  function start() {
    scan(document);
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
